import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Badge from '@mui/material/Badge';
import GlobalStyles from '@mui/material/GlobalStyles';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Business, Email, Dashboard, Logout, People, Sms, Group, AdminPanelSettings, Security, Storage, Token, Queue } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import CompanySwitcher from '../company/CompanySwitcher';
import { useLockedAccounts } from '../../hooks/useLockedAccounts';

const drawerWidth = 240;

const Layout = () => {
  const navigate = useNavigate();
  const { user, selectedCompany } = useAuth();
  const { lockedAccountsCount } = useLockedAccounts();
  const [showTwoFactorBanner, setShowTwoFactorBanner] = useState(false);

  useEffect(() => {
    // Check if we should show 2FA setup suggestion
    const suggestSetup = sessionStorage.getItem('suggestTwoFactorSetup');
    console.log('Layout useEffect - suggestTwoFactorSetup:', suggestSetup);
    if (suggestSetup === 'true') {
      console.log('Setting showTwoFactorBanner to true');
      setShowTwoFactorBanner(true);
      // Remove the flag so it doesn't show again
      sessionStorage.removeItem('suggestTwoFactorSetup');
    }
  }, []);

  const handleCloseTwoFactorBanner = () => {
    setShowTwoFactorBanner(false);
  };

  const handleSetupTwoFactor = () => {
    navigate('/setup-2fa');
  };

  const handleLogout = () => {
    // Navigate to centralized logout route for consistent UX
    navigate('/auth/logout');
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const menuItems = React.useMemo(() => {
    if (!user) return [];
    
    const items = [
      { text: 'Dashboard', icon: <Dashboard />, path: '/' },
      { text: 'Email Config', icon: <Email />, path: '/email-config' },
      { text: 'SMS Logs', icon: <Sms />, path: '/sms-logs' },
      { text: 'Account Settings', icon: <Security />, path: '/settings' },
    ];

    if (isAdmin) {
      items.push(
        { text: 'Companies', icon: <Business />, path: '/companies' },
        { text: 'Users', icon: <People />, path: '/users' },
        { text: 'Group Management', icon: <Group />, path: '/admin/groups' },
        { text: 'User Management', icon: <AdminPanelSettings />, path: '/admin/user-companies' },
        { 
          text: 'Account Security', 
          icon: lockedAccountsCount > 0 ? (
            <Badge badgeContent={lockedAccountsCount} color="error">
              <Security />
            </Badge>
          ) : (
            <Security />
          ), 
          path: '/admin/locked-accounts' 
        },
        { text: 'SMTP Pool Management', icon: <Storage />, path: '/admin/smtp-pools' },
        { text: 'Token Management', icon: <Token />, path: '/admin/tokens' },
        { text: 'Session Management', icon: <Security />, path: '/admin/sessions' },
        { text: 'Queue Management', icon: <Queue />, path: '/admin/queue' }
      );
    }

    return items;
  }, [user, isAdmin, lockedAccountsCount]);

  return (
    <>
      <GlobalStyles
        styles={{
          '@keyframes pulse': {
            '0%': { opacity: 1 },
            '50%': { opacity: 0.6 },
            '100%': { opacity: 1 }
          }
        }}
      />
      <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <img 
            src='/assets/aptean.png' 
            alt='Aptean Logo' 
            style={{ height: '40px', marginRight: '16px' }}
          />
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Swords iCom Service
          </Typography>
          
          {/* Company Switcher - Only show for regular users, not admins */}
          {!isAdmin && (
            <Box sx={{ mr: 2 }}>
              <CompanySwitcher />
            </Box>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ color: 'white', fontSize: '0.875rem' }}>
                {user?.email}
              </Typography>
              {/* Show group name for admin users */}
              {isAdmin && user?.groups && user.groups.length > 0 && (
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Group: {user.groups.map(g => g.name).join(', ')}
                </Typography>
              )}
              {selectedCompany && (
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {selectedCompany.name}
                </Typography>
              )}
            </Box>
            {isAdmin && (
              <Chip
                label="Admin"
                size="small"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 'bold'
                }}
              />
            )}
            {/* Show locked accounts notification for admins */}
            {isAdmin && lockedAccountsCount > 0 && (
              <Chip
                label={`${lockedAccountsCount} Locked Account${lockedAccountsCount !== 1 ? 's' : ''}`}
                size="small"
                color="error"
                sx={{
                  fontWeight: 'bold',
                  animation: 'pulse 2s infinite'
                }}
              />
            )}
            <Button
              color="inherit"
              onClick={handleLogout}
              startIcon={<Logout />}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.text} component={Link} to={item.path}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar />
        {showTwoFactorBanner && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  color="info"
                  size="small"
                  onClick={handleSetupTwoFactor}
                  startIcon={<Security />}
                >
                  Set Up Now
                </Button>
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={handleCloseTwoFactorBanner}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            }
          >
            <strong>Enhance your account security!</strong> Two-factor authentication is recommended for your account. Set it up now to protect your data.
          </Alert>
        )}
        <Outlet />
      </Box>
      </Box>
    </>
  );
};

export default Layout;
